import {useEffect, useState} from 'react';

import {BREAKPOINTS} from 'client/constants';

const ALL_BREAKPOINTS: number[] = [
  BREAKPOINTS.xxs,
  BREAKPOINTS.xs,
  BREAKPOINTS.s,
  BREAKPOINTS.m,
  BREAKPOINTS.l,
  BREAKPOINTS.xl
];

export type Breakpoint = 'base' | 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl';

function doesBreakpointMatch(breakpoint: number) {
  return typeof window === 'undefined'
    ? false
    : window.matchMedia(`(width >= ${breakpoint}px)`).matches;
}

function getCurrentBreakpoint(): Breakpoint {
  if (typeof window === 'undefined') {
    return 'base';
  } else if (doesBreakpointMatch(BREAKPOINTS.xl)) {
    return 'xl';
  } else if (doesBreakpointMatch(BREAKPOINTS.l)) {
    return 'l';
  } else if (doesBreakpointMatch(BREAKPOINTS.m)) {
    return 'm';
  } else if (doesBreakpointMatch(BREAKPOINTS.s)) {
    return 's';
  } else if (doesBreakpointMatch(BREAKPOINTS.xs)) {
    return 'xs';
  } else if (doesBreakpointMatch(BREAKPOINTS.xxs)) {
    return 'xxs';
  }

  return 'base';
}

export default function useBreakpoint() {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(getCurrentBreakpoint);

  const isBase = breakpoint === 'base';
  const isXxs = breakpoint === 'xxs';
  const isXs = breakpoint === 'xs';
  const isS = breakpoint === 's';
  const isM = breakpoint === 'm';
  const isL = breakpoint === 'l';
  const isXl = breakpoint === 'xl';

  const isXxsAndUp = breakpoint !== 'base';
  const isXsAndUp = ['xs', 's', 'm', 'l', 'xl'].includes(breakpoint);
  const isSAndUp = ['s', 'm', 'l', 'xl'].includes(breakpoint);
  const isMAndUp = ['m', 'l', 'xl'].includes(breakpoint);
  const isLAndUp = ['l', 'xl'].includes(breakpoint);

  const isXxsAndDown = ['base', 'xxs'].includes(breakpoint);
  const isXsAndDown = ['base', 'xxs', 'xs'].includes(breakpoint);
  const isSAndDown = ['base', 'xxs', 'xs', 's'].includes(breakpoint);
  const isMAndDown = ['base', 'xxs', 'xs', 's', 'm'].includes(breakpoint);
  const isLAndDown = breakpoint !== 'xl';

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    function handleChange() {
      setBreakpoint(getCurrentBreakpoint());
    }

    const mediaQueryLists = ALL_BREAKPOINTS.map((bp) => window.matchMedia(`(min-width: ${bp}px)`));

    mediaQueryLists.forEach((mediaQueryList) => {
      mediaQueryList.addEventListener('change', handleChange);
    });

    return () => {
      mediaQueryLists.forEach((mediaQueryList) => {
        mediaQueryList.removeEventListener('change', handleChange);
      });
    };
  }, []);

  return {
    breakpoint,
    // Breakpoint equality
    isBase,
    isXxs,
    isXs,
    isS,
    isM,
    isL,
    isXl,
    // Breakpoint and up
    isXxsAndUp,
    isXsAndUp,
    isSAndUp,
    isMAndUp,
    isLAndUp,
    // Breakpoint and down
    isXxsAndDown,
    isXsAndDown,
    isSAndDown,
    isMAndDown,
    isLAndDown
  };
}
